import { Component, CUSTOM_ELEMENTS_SCHEMA, forwardRef, Input, OnInit } from '@angular/core';
import { ConfigGeoRegions } from '../../../config/geo-regions';
import { AbstractValueAccessor } from '../abstract-value-accessor';
import { AbstractControl, NG_VALUE_ACCESSOR, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { SharedModule } from '../shared.module';

export interface GeoRegion
{
    countryCode: string;
    region: string;
    abbreviation?: string;
}

export enum GeoRegionValues {
    BOTH = 'both',
    REGION = 'region',
    ABBREVIATION = 'abbreviation',
}

/**
 * The option a stored value stands for, or null when it stands for none. A stored state is a full
 * name ("Virginia") or an abbreviation, in any case. `countryCode` is preferred when it narrows the
 * match, because an abbreviation alone is not unique across countries.
 */
export function resolveGeoRegion(value: GeoRegion | string, statesOnly = false): GeoRegion | null {
    if (!value) {
        return null;
    }
    const wanted = (typeof value === 'string' ? value : value.abbreviation || value.region || '').trim().toLowerCase();
    const alsoWanted = typeof value === 'string' ? '' : (value.region || '').trim().toLowerCase();
    const countryCode = typeof value === 'string' || statesOnly ? 'US' : value.countryCode;
    if (!wanted) {
        return null;
    }
    const pool = statesOnly
            ? ConfigGeoRegions.RegionsByCountry[0].regions
            : ConfigGeoRegions.RegionsByCountry.flatMap((country) => country.regions);
    const matches = (region: GeoRegion) => [wanted, alsoWanted].some((name) => name
            && (region.region.toLowerCase() === name || region.abbreviation?.toLowerCase() === name));
    return pool.find((region) => (!countryCode || region.countryCode === countryCode) && matches(region))
            ?? pool.find(matches)
            ?? null;
}

/**
 * Required, for a control bound to lf-geo-region-selector. `Validators.required` only checks for a
 * non-null value, and the address forms patch `{countryCode, region}` from the profile even when the
 * saved state is blank, so the selector shows nothing while the form still counts as valid. This
 * fails whenever a selector without `statesOnly` would show no option.
 */
export function geoRegionRequired(control: AbstractControl): ValidationErrors | null {
    return resolveGeoRegion(control.value) ? null : { required: true };
}

export const GEO_REGION_INPUT_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => GeoRegionSelectorComponent),
    multi: true,
};

@Component({
    selector: 'lf-geo-region-selector',
    styleUrls: ['./geo-region-selector.scss'],
    templateUrl: './geo-region-selector.component.html',
    providers: [GEO_REGION_INPUT_VALUE_ACCESSOR],
    standalone: true,
    imports: [
        // Shared
        SharedModule,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class GeoRegionSelectorComponent extends AbstractValueAccessor<GeoRegion> implements OnInit
{
    @Input() placeholder = '';
    @Input() readonly = false;
    @Input() tabindex = 0;

    @Input() useMatSelect = false;
    @Input() formGroup: UntypedFormGroup;
    @Input() controlName = 'region';
    @Input() disabled = false;

    @Input() valueType: GeoRegionValues = GeoRegionValues.BOTH;
    @Input() displayType: GeoRegionValues = GeoRegionValues.REGION;
    @Input() statesOnly = false;
    @Input() size: 'large' | 'medium' | 'small' | 'xsmall';

    readonly countries: {
        country: string,
        regions: GeoRegion[]
    }[] = ConfigGeoRegions.RegionsByCountry;
    readonly states: GeoRegion[] = ConfigGeoRegions.RegionsByCountry[0].regions;
    readonly stateAbbreviations = ConfigGeoRegions.RegionsByCountry[0].regions.map((state) => state.abbreviation);
    readonly regionValues = GeoRegionValues;

    ngOnInit() {
    }

    /**
     * ph-select carries a string; this control's value is a GeoRegion object (or
     * a bare abbreviation when `statesOnly`). `countryCode|abbreviation` is the
     * key both directions, because an abbreviation alone is not unique across
     * countries — ON is Ontario and also a US state abbreviation in some lists.
     */
    get selectItems(): { value: string; label: string; group?: string }[] {
        if (this.statesOnly) {
            return this.states.map((state) => ({
                value: state.abbreviation,
                label: this.regionLabel(state),
            }));
        }

        const items: { value: string; label: string; group?: string }[] = [];
        this.countries.forEach((country) => {
            country.regions.forEach((region) => {
                items.push({ value: this.regionKey(region), label: this.regionLabel(region), group: country.country });
            });
        });
        return items;
    }

    /** Callers that pass `formGroup` + `controlName` own the value there, not through ngModel/formControlName. */
    get boundControl(): AbstractControl | null {
        return this.useMatSelect && this.formGroup ? this.formGroup.get(this.controlName) : null;
    }

    get selectedKey(): string | null {
        const current = this.boundControl ? this.boundControl.value : this._value;
        if (!current) {
            return null;
        }
        const region = resolveGeoRegion(current, this.statesOnly);
        if (!region) {
            return null;
        }
        return this.statesOnly ? region.abbreviation : this.regionKey(region);
    }

    onRegionSelected(key: string): void {
        let picked: GeoRegion | string = null;
        if (this.statesOnly) {
            picked = this.stateAbbreviations.includes(key) ? key : null;
        } else {
            this.countries.forEach((country) => {
                country.regions.forEach((region) => {
                    if (this.regionKey(region) === key) {
                        picked = region;
                    }
                });
            });
        }

        const control = this.boundControl;
        if (control) {
            control.setValue(picked);
            control.markAsDirty();
            control.markAsTouched();
            return;
        }
        this.writeValue(picked as GeoRegion);
    }

    private regionKey(region: GeoRegion): string {
        return `${region.countryCode}|${region.abbreviation || region.region}`;
    }

    private regionLabel(region: GeoRegion): string {
        if (this.displayType === GeoRegionValues.ABBREVIATION) {
            return region.abbreviation || region.region;
        }
        if (this.displayType === GeoRegionValues.REGION || !region.abbreviation) {
            return region.region;
        }
        return `${region.region} (${region.abbreviation})`;
    }

    writeValue(value: GeoRegion) {
        value = this.findRegionOption(value);

        if (value !== this._value) {
            this._value = value;
            this.propagateChange(this._value);
        }
    }

    findRegionOption(value: GeoRegion) {
        if (!value) {
            return null;
        }
        if (typeof value === 'string') {
            return this.stateAbbreviations.includes(value) ? value : null;
        }

        let targetRegion = null;
        this.countries.forEach((country) => {
            country.regions.forEach((region) => {
                if (region.countryCode === value.countryCode && region.region === value.region) {
                    targetRegion = region;
                }
            });
        });

        return targetRegion;
    }

    abbreviationsComparator(s1: string, s2: string) {
        return s1 && s2 && s1 === s2;
    }

    comparator(g1: GeoRegion, g2: GeoRegion) {
        return g1 && g2 && g1.region === g2.region && g1.countryCode === g2.countryCode;
    }
}
